(function (Drupal, once) {
  'use strict';

  const MENU_SELECTOR = '#block-adocs-verlag, #block-adocs-legal-content';
  const cleanups = new WeakMap();

  Drupal.behaviors.publisherMobileMenu = {
    attach(context) {
      once(
        'publisher-mobile-menu',
        MENU_SELECTOR,
        context
      ).forEach((menu) => {
        const initialContent = document.querySelector('#block-adocs-content');

        if (!initialContent) {
          return;
        }

        const mobileQuery = window.matchMedia('(max-width: 1023px)');
        const isLegalMenu = menu.id === 'block-adocs-legal-content';
        const listeners = new AbortController();
        const listenerOptions = { signal: listeners.signal };
        let activation = null;

        const ICON_DOWN = '#arrow_down';
        const ICON_UP = '#arrow_up';

        // Open the current page on initial load so navigation from elsewhere,
        // including footer links, reveals its content in the accordion.
        const OPEN_CURRENT_ITEM_ON_INIT = true;

        const initialParent = initialContent.parentNode;
        const initialNextSibling = initialContent.nextSibling;
        const initialUrl = initialContent.dataset.publisherContextualDestination || window.location.href;
        const initialPath = new URL(initialUrl).pathname;

        const items = Array.from(
          menu.querySelectorAll(':scope > .menu > .menu-item')
        );

        /**
         * Get the direct menu link.
         */
        const getLink = (item) => item.querySelector(':scope > a');

        /**
         * Only entity links have page content that belongs in the accordion.
         * The legal menu also contains the consent-manager link, which must
         * retain its original Klaro behavior.
         */
        const isContentLink = (link) =>
          /^node\/\d+$/.test(link?.dataset.drupalLinkSystemPath || '');

        /**
         * Get the content wrapper belonging to an item.
         */
        const getWrapper = (item) =>
          item.querySelector(':scope > .publisher-mobile-content');

        /**
         * Generate a stable ID for the accordion content.
         */
        const getContentId = (item) => {
          const link = getLink(item);
          const systemPath = link?.dataset.drupalLinkSystemPath || '';

          const nid = systemPath.match(/^node\/(\d+)$/)?.[1];

          return nid
            ? `publisher-mobile-content-${nid}`
            : `publisher-mobile-content-${items.indexOf(item)}`;
        };

        /**
         * Move a generated legal-page table of contents into the active menu
         * item, matching the desktop and mobile Sketch navigation.
         */
        const prepareLegalToc = (content, item) => {
          if (!isLegalMenu || !content || !item) {
            return;
          }

          const sourceRoot = content.querySelector('[data-publisher-toc]');
          const sourceList = sourceRoot?.querySelector('ol');
          if (!sourceRoot || !sourceList) return;

          sourceRoot?.classList.add('legal-generated-toc');

          let navigationToc = item.querySelector(
            ':scope > .legal-navigation__toc'
          );

          if (!navigationToc) {
            navigationToc = document.createElement('div');
            navigationToc.className = 'legal-navigation__toc';
            navigationToc.appendChild(sourceList.cloneNode(true));
            item.appendChild(navigationToc);
          }
        };

        /**
         * Create an SVG icon using the existing sprite.
         */
        const createIcon = () => {
          const namespace = 'http://www.w3.org/2000/svg';

          const svg = document.createElementNS(namespace, 'svg');
          const use = document.createElementNS(namespace, 'use');

          svg.classList.add('publisher-menu-icon');
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');

          use.setAttribute('href', ICON_DOWN);

          svg.appendChild(use);

          return svg;
        };

        /**
         * Change the arrow on a menu item.
         */
        const setIconState = (item, open) => {
          const link = getLink(item);
          const use = link?.querySelector('.publisher-menu-icon use');

          if (use) {
            use.setAttribute('href', open ? ICON_UP : ICON_DOWN);
          }
        };

        /**
         * Prepare menu links for the mobile accordion.
         */
        items.forEach((item) => {
          const link = getLink(item);

          if (!link) {
            return;
          }

          const contentId = getContentId(item);

          if (!link.querySelector('.publisher-menu-icon')) {
            link.appendChild(createIcon());
          }

          if (isContentLink(link)) {
            link.setAttribute('aria-controls', contentId);
            link.setAttribute('aria-expanded', 'false');
          }
        });

        /**
         * Create a wrapper for rendered Drupal content.
         */
        const createContentWrapper = (item, content) => {
          const wrapper = document.createElement('div');

          // Keep each content's origin when other accordion items change the URL.
          content.dataset.publisherContextualDestination = content === initialContent
            ? initialUrl
            : getLink(item).href;

          wrapper.className = 'publisher-mobile-content';
          wrapper.id = getContentId(item);
          wrapper.hidden = true;

          // The menu link already serves as the accessible label.
          wrapper.setAttribute('role', 'region');

          wrapper.appendChild(content);

          return wrapper;
        };

        /**
         * Close one item.
         */
        const closeItem = (item) => {
          const wrapper = getWrapper(item);
          const link = getLink(item);

          item.classList.remove('publisher-menu-item--open');

          if (wrapper) {
            wrapper.hidden = true;
          }

          if (link) {
            link.setAttribute('aria-expanded', 'false');
          }

          setIconState(item, false);
        };

        /**
         * Close all items except an optional one.
         */
        const closeAll = (except = null) => {
          items.forEach((item) => {
            if (item !== except) {
              closeItem(item);
            }
          });
        };

        /**
         * Update Drupal's visual active-link state after pushState/popstate.
         */
        const updateActiveState = (activeItem) => {
          items.forEach((item) => {
            const link = getLink(item);
            const active = item === activeItem;

            item.classList.toggle('menu-item--active-trail', active);

            if (!link) {
              return;
            }

            link.classList.toggle('is-active', active);

            if (active) {
              link.setAttribute('aria-current', 'page');
            }
            else {
              link.removeAttribute('aria-current');
            }
          });
        };

        /**
         * Open an already loaded item.
         */
        const openItem = (item, options = {}) => {
          const {
            updateActive = true,
          } = options;

          const wrapper = getWrapper(item);
          const link = getLink(item);

          if (!wrapper || !link) {
            return;
          }

          // Remember the item's current position in the viewport.
          // Closing another open item above it may otherwise make it jump upwards.
          const topBefore = item.getBoundingClientRect().top;

          closeAll(item);

          wrapper.hidden = false;
          item.classList.add('publisher-menu-item--open');

          link.setAttribute('aria-expanded', 'true');

          setIconState(item, true);

          if (updateActive) {
            updateActiveState(item);
          }

          // Keep the clicked menu item at exactly the same viewport position.
          const topAfter = item.getBoundingClientRect().top;

          window.scrollBy({
            top: topAfter - topBefore,
            left: 0,
            behavior: 'instant',
          });
        };

        /**
         * Set/unset the loading state of an item.
         */
        const setLoading = (item, loading) => {
          const link = getLink(item);

          item.classList.toggle(
            'publisher-menu-item--loading',
            loading
          );

          if (link) {
            if (loading) {
              link.setAttribute('aria-busy', 'true');
            }
            else {
              link.removeAttribute('aria-busy');
            }
          }
        };

        /**
         * Fetch one publisher page.
         */
        const loadContent = async (url, signal) => {
          const response = await fetch(url, {
            signal,
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (!response.ok) {
            throw new Error(
              `Could not load publisher page (${response.status}).`
            );
          }

          const html = await response.text();

          const fetchedDocument = new DOMParser().parseFromString(
            html,
            'text/html'
          );

          const fetchedContent = fetchedDocument.querySelector(
            '#block-adocs-content'
          );

          if (!fetchedContent) {
            throw new Error(
              'Loaded page contains no #block-adocs-content.'
            );
          }

          // The original ID must not occur multiple times in the live DOM.
          fetchedContent.removeAttribute('id');

          return fetchedContent;
        };

        // A single activation owns loading, opening and history. Cancel it even
        // when the next action opens cached content or closes the current item.
        const cancelActivation = () => {
          if (!activation) return;
          activation.controller.abort();
          setLoading(activation.item, false);
          activation = null;
        };

        /**
         * Load an item if necessary; only the latest selection may activate it.
         */
        const activateItem = async (
          item,
          {
            pushHistory = true,
          } = {}
        ) => {
          const link = getLink(item);

          if (!link) {
            return;
          }

          cancelActivation();
          const current = { item, controller: new AbortController() };
          activation = current;
          const isCurrent = () => activation === current
            && !listeners.signal.aborted && mobileQuery.matches;
          let wrapper = getWrapper(item);

          if (!wrapper) {
            setLoading(item, true);

            try {
              const fetchedContent = await loadContent(link.href, current.controller.signal);
              if (!isCurrent()) return;

              prepareLegalToc(fetchedContent, item);

              wrapper = createContentWrapper(item, fetchedContent);
              item.appendChild(wrapper);

              // Initialize Drupal behaviors in the newly inserted content.
              Drupal.attachBehaviors(wrapper);
            }
            catch (error) {
              if (!isCurrent()) return;
              cancelActivation();
              console.error(error);

              // Progressive-enhancement fallback:
              // if AJAX fails, use the ordinary page navigation.
              window.location.assign(link.href);

              return;
            }
            finally {
              if (isCurrent()) setLoading(item, false);
            }
          }

          if (!isCurrent()) return;
          openItem(item, {
            updateActive: true,
          });

          if (pushHistory) {
            window.history.pushState(
              {
                publisherPath: new URL(link.href).pathname,
              },
              '',
              link.href
            );
          }
          activation = null;
        };

        /**
         * Find the menu item belonging to a route path.
         */
        const findItemForPath = (path) => {
          return items.find((item) => {
            const link = getLink(item);

            if (!link) {
              return false;
            }

            return new URL(link.href).pathname === path;
          });
        };
        const findItemForCurrentPath = () => findItemForPath(window.location.pathname);

        /**
         * Put the originally rendered Drupal content into its menu item.
         *
         * Depending on OPEN_CURRENT_ITEM_ON_INIT, either open the matching
         * accordion item immediately or keep it collapsed.
         */
        const initializeMobileContent = () => {
          const item =
            findItemForPath(initialPath) ||
            menu.querySelector('.menu-item--active-trail');

          if (!item) {
            return;
          }

          prepareLegalToc(initialContent, item);

          let wrapper = getWrapper(item);

          if (!wrapper) {
            wrapper = createContentWrapper(item, initialContent);
            item.appendChild(wrapper);
          }

          // Keep the current menu item visually active regardless of whether
          // its accordion content is initially expanded or collapsed.
          const currentItem = findItemForCurrentPath() || item;
          if (currentItem !== item) {
            activateItem(currentItem, { pushHistory: false });
            return;
          }
          updateActiveState(item);

          if (OPEN_CURRENT_ITEM_ON_INIT) {
            openItem(item, {
              updateActive: true,
            });
          }
          else {
            closeItem(item);
          }
        };

        /**
         * Restore Drupal's original desktop DOM structure.
         */
        const restoreDesktopLayout = (reload = true) => {
          // AJAX navigation changes the URL, but initialContent still belongs
          // to the original page. Render the current route on desktop instead.
          if (reload && window.location.pathname !== initialPath) {
            window.location.reload();
            return;
          }
          items.forEach((item) => {
            closeItem(item);

            const link = getLink(item);

            if (link) {
              link.removeAttribute('aria-expanded');
              link.removeAttribute('aria-controls');
              link.removeAttribute('aria-busy');
            }
          });

          const initialWrapper = initialContent.closest(
            '.publisher-mobile-content'
          );

          if (initialWrapper) {
            if (
              initialNextSibling &&
              initialNextSibling.parentNode === initialParent
            ) {
              initialParent.insertBefore(
                initialContent,
                initialNextSibling
              );
            }
            else {
              initialParent.appendChild(initialContent);
            }

            initialWrapper.remove();
          }

          initialContent.hidden = false;
        };

        /**
         * Re-enable mobile accessibility attributes after returning
         * from desktop mode.
         */
        const prepareMobileLinks = () => {
          items.forEach((item) => {
            const link = getLink(item);

            if (!link) {
              return;
            }

            if (!isContentLink(link)) {
              return;
            }

            link.setAttribute(
              'aria-controls',
              getContentId(item)
            );

            link.setAttribute('aria-expanded', 'false');
          });
        };

        /**
         * Menu clicks.
         */
        items.forEach((item) => {
          const link = getLink(item);

          if (!link) {
            return;
          }

          link.addEventListener('click', (event) => {
            if (!mobileQuery.matches || !isContentLink(link)) {
              return;
            }

            event.preventDefault();

            if (
              item.classList.contains(
                'publisher-menu-item--loading'
              )
            ) {
              return;
            }

            if (
              item.classList.contains(
                'publisher-menu-item--open'
              )
            ) {
              cancelActivation();
              closeItem(item);

              return;
            }

            activateItem(item);
          }, listenerOptions);
        });

        /**
         * Browser back/forward.
         */
        const onPopState = () => {
          if (!mobileQuery.matches) {
            return;
          }

          const item = findItemForCurrentPath();

          if (item) {
            activateItem(item, {
              pushHistory: false,
            });
          }
          else cancelActivation();
        };
        window.addEventListener('popstate', onPopState, listenerOptions);

        /**
         * Switch between desktop and mobile.
         */
        const updateLayout = (event) => {
          cancelActivation();
          if (event.matches) {
            prepareMobileLinks();
            initializeMobileContent();
          }
          else {
            prepareLegalToc(
              initialContent,
              findItemForCurrentPath() ||
                menu.querySelector('.menu-item--active-trail')
            );
            restoreDesktopLayout();
          }
        };

        updateLayout(mobileQuery);

        mobileQuery.addEventListener(
          'change',
          updateLayout,
          listenerOptions
        );
        cleanups.set(menu, (settings) => {
          listeners.abort();
          cancelActivation();
          restoreDesktopLayout(false);
          // Fetched panels own attached behaviors; release them before removal.
          items.forEach((item) => {
            const wrapper = getWrapper(item);
            if (wrapper) {
              Drupal.detachBehaviors(wrapper, settings, 'unload');
              wrapper.remove();
            }
          });
        });
      });
    },
    detach(context, settings, trigger) {
      if (trigger !== 'unload') return;
      // Drupal may detach the menu itself or an ancestor containing it.
      const menus = [...context.querySelectorAll(MENU_SELECTOR)];
      if (context.matches?.(MENU_SELECTOR)) menus.unshift(context);
      once.remove('publisher-mobile-menu', menus).forEach((menu) => {
        cleanups.get(menu)?.(settings);
        cleanups.delete(menu);
      });
    },
  };

})(Drupal, once);
